import { Api_URI } from "./index";

export async function listCopilotSessions({ token, role }) {
  const res = await fetch(`${Api_URI}/api/${role}/copilot/sessions`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function getCopilotSession({ token, role, sessionId }) {
  const res = await fetch(
    `${Api_URI}/api/${role}/copilot/sessions/${encodeURIComponent(sessionId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Voice dictation → text. Sends the recorded blob to the backend, which
 * forwards it to OpenAI transcription (gpt-4o-mini-transcribe / whisper-1).
 * @returns {Promise<{text: string, model?: string}>}
 */
export async function transcribeAudio({ token, role, blob, filename, signal }) {
  const fd = new FormData();
  fd.append("audio", blob, filename || "dictation.webm");
  const res = await fetch(`${Api_URI}/api/${role}/copilot/transcribe`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
    signal,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

/**
 * Rewrite a raw dictation transcript into a professional email reply body.
 * @returns {Promise<{formalized: string}>}
 */
export async function formalizeTranscript({ token, role, transcript, context, signal }) {
  const res = await fetch(`${Api_URI}/api/${role}/copilot/formalize`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ transcript, context }),
    signal,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

/**
 * Send a reply to the customer on a quote's email thread. The body/cc/bcc were
 * drafted + confirmed by the operator; this fires the real authenticated send.
 * Returns { ok, error? } — a 200 { ok:false, error } is a *soft* failure (e.g.
 * "no email thread on this quote"), so we surface the JSON rather than throw.
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export async function copilotReply({ token, role, quoteId, body, cc, bcc, signal }) {
  const res = await fetch(`${Api_URI}/api/${role}/copilot/reply`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ quoteId, body, cc: cc || [], bcc: bcc || [] }),
    signal,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, error: data?.error || `HTTP ${res.status}` };
  }
  return data;
}

/**
 * Send rate requests to the named carriers on a quote.
 * @returns {Promise<{ok: boolean, results?: Array<{carrier, ok, error?}>, error?: string}>}
 */
export async function copilotRequestRates({ token, role, quoteId, carriers, note, signal }) {
  const res = await fetch(`${Api_URI}/api/${role}/copilot/requestRates`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ quoteId, carriers: carriers || [], note }),
    signal,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, error: data?.error || `HTTP ${res.status}`, results: [] };
  }
  return data;
}

/**
 * Fetch 0-2 proactive nudges for a quote (things worth doing next). Never
 * throws — a failed fetch just yields no nudges so the FAB stays quiet.
 * @returns {Promise<{nudges: Array<{text, suggestedPrompt}>}>}
 */
export async function copilotNudges({ token, role, quoteId, signal }) {
  try {
    const res = await fetch(
      `${Api_URI}/api/${role}/copilot/nudges?quoteId=${encodeURIComponent(quoteId)}`,
      { headers: { Authorization: `Bearer ${token}` }, signal }
    );
    if (!res.ok) return { nudges: [] };
    const data = await res.json().catch(() => ({}));
    return { nudges: Array.isArray(data?.nudges) ? data.nudges : [] };
  } catch {
    return { nudges: [] };
  }
}

/**
 * Send ONE confirmed follow-up message to the customers of many quotes.
 * items were pre-resolved by the confirm card; the backend re-checks access
 * and caps at 20 regardless.
 * @returns {Promise<{ok: boolean, sent?: number, failed?: number, results?: Array, error?: string}>}
 */
export async function copilotBulkChase({ token, role, items, body, signal }) {
  const res = await fetch(`${Api_URI}/api/${role}/copilot/bulkChase`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ items, body }),
    signal,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, error: data?.error || `HTTP ${res.status}`, results: [] };
  }
  return data;
}

/**
 * Move many quotes to a new stage after the operator confirmed the card.
 * @returns {Promise<{ok: boolean, updated?: number, failed?: number, results?: Array, error?: string}>}
 */
export async function copilotBulkStage({ token, role, items, newStage, signal }) {
  const res = await fetch(`${Api_URI}/api/${role}/copilot/bulkStage`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ items, newStage }),
    signal,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, error: data?.error || `HTTP ${res.status}`, results: [] };
  }
  return data;
}

/**
 * Zero-token quote KPIs (by-stage counts, win rate, volume, revenue, stale
 * count) scoped to the caller's inboxes. Never throws — failures yield null so
 * dashboards can just skip the widget.
 * @returns {Promise<object|null>}
 */
export async function getCopilotAnalytics({ token, role, params, signal }) {
  try {
    const qs = new URLSearchParams(params || {}).toString();
    const res = await fetch(
      `${Api_URI}/api/${role}/copilot/analytics${qs ? `?${qs}` : ""}`,
      { headers: { Authorization: `Bearer ${token}` }, signal }
    );
    if (!res.ok) return null;
    return await res.json().catch(() => null);
  } catch {
    return null;
  }
}

/**
 * Fetch one email attachment (by its number from the attachments card) as a
 * Blob for in-browser preview. Returns null on any failure — the caller shows
 * a chip instead of a broken tab.
 * @returns {Promise<Blob|null>}
 */
export async function fetchCopilotAttachment({ token, role, quoteId, index, filename, signal }) {
  try {
    const params = { quoteId };
    if (index != null) params.index = String(index);
    if (filename) params.filename = filename;
    const qs = new URLSearchParams(params).toString();
    const res = await fetch(`${Api_URI}/api/${role}/copilot/attachment?${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    if (!res.ok) return null;
    return await res.blob();
  } catch {
    return null;
  }
}

/**
 * Analyze a LOCAL document (drag & drop / paperclip) — no quote needed.
 * @returns {Promise<{filename?: string, kind?: string, analysis?: string, error?: string}>}
 */
export async function analyzeCopilotUpload({ token, role, file, question, signal }) {
  try {
    const form = new FormData();
    form.append("document", file, file.name);
    if (question) form.append("question", question);
    const res = await fetch(`${Api_URI}/api/${role}/copilot/analyzeUpload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
      signal,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { error: data?.error || data?.message || `HTTP ${res.status}` };
    }
    return data;
  } catch {
    return { error: "Upload failed — check your connection and try again." };
  }
}

/**
 * @param {object} opts
 * @yields {object} parsed SSE data lines
 */
export async function* streamCopilotChat({
  token,
  role,
  sessionId,
  messages,
  pageContext,
  signal,
}) {
  const res = await fetch(`${Api_URI}/api/${role}/copilot/chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ sessionId, messages, pageContext }),
    signal,
  });
  if (!res.ok || !res.body) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop() || "";
    for (const line of lines) {
      const trimmed = line.replace(/^data:\s*/, "").trim();
      if (!trimmed) continue;
      try {
        yield JSON.parse(trimmed);
      } catch {
        /* ignore */
      }
    }
  }
}
